import { useEffect, useState } from "react";
import { api } from "../lib/api";

// ── Constants ─────────────────────────────────────────────────────────────────

const PLAN_PRICE: Record<string, number> = {
  core:  1000,
  pro:   1500,
  ultra: 2000,
};

// Los badges de PLAN conservan su fondo tintado suave (etiqueta funcional, sin gradientes).
const PLAN_LABEL: Record<string, { name: string; level: string; bg: string; color: string }> = {
  core:  { name: "Core",  level: "Nivel 1", bg: "#3b82f620", color: "#3b82f6" },
  pro:   { name: "Pro",   level: "Nivel 2", bg: "#8b5cf620", color: "#8b5cf6" },
  ultra: { name: "Ultra", level: "Nivel 3", bg: "#f59e0b20", color: "#f59e0b" },
};

const CHART_START = new Date(2026, 0, 1); // Enero 2026 — inicio del eje del gráfico

const MONTH_NAMES       = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const MONTH_NAMES_LONG  = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

// ── Types ─────────────────────────────────────────────────────────────────────

interface Org {
  id: string;
  name: string;
  plan: string;
  is_active: boolean;
  billing_start: string | null;
  created_at: string;
}

interface MonthData {
  year: number;
  month: number;
  label: string;
  shortLabel: string;
  revenue: number;
  byPlan: Record<string, number>; // € facturados ese mes por cada plan
  billed: boolean;
}

interface Invoice {
  number: string;   // FAC-2026-01-001
  period: string;   // "Enero 2026"
  issueDate: Date;  // last day of month
  amount: number;
  status: "paid" | "pending";
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function lastDayOfMonth(year: number, month: number): Date {
  return new Date(year, month + 1, 0);
}

function formatEur(n: number): string {
  return n.toLocaleString("es-ES") + " €";
}

function formatEurShort(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(".", ",") + " M€";
  if (n >= 1_000)     return (n / 1_000).toFixed(n % 1000 === 0 ? 0 : 1).replace(".", ",") + " k€";
  return n + " €";
}

function formatDate(d: Date): string {
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

/** Primer día del mes en que la org empezó a facturar (billing_start tiene prioridad sobre created_at) */
function orgBillingStart(org: Org): Date {
  const src = org.billing_start ?? org.created_at;
  const d = new Date(src);
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function buildMonths(orgs: Org[], now: Date): MonthData[] {
  const months: MonthData[] = [];
  const cur = new Date(now.getFullYear(), now.getMonth(), 1);
  const d   = new Date(CHART_START);
  while (d <= cur) {
    const y = d.getFullYear(), m = d.getMonth();
    const monthStart = new Date(y, m, 1);
    const last       = lastDayOfMonth(y, m);
    // Solo cuentan orgs activas cuya fecha de alta es ≤ este mes
    const byPlan: Record<string, number> = {};
    orgs
      .filter(o => o.is_active && orgBillingStart(o) <= monthStart)
      .forEach(o => { byPlan[o.plan] = (byPlan[o.plan] ?? 0) + (PLAN_PRICE[o.plan] ?? 0); });
    const revenue = Object.values(byPlan).reduce((s, v) => s + v, 0);
    months.push({
      year: y, month: m,
      label:      `${MONTH_NAMES_LONG[m]} ${y}`,
      shortLabel: MONTH_NAMES[m],
      revenue,
      byPlan,
      billed: last < now,
    });
    d.setMonth(d.getMonth() + 1);
  }
  return months;
}

function buildInvoices(org: Org, orgIndex: number, now: Date): Invoice[] {
  const invoices: Invoice[] = [];
  const start = orgBillingStart(org);           // empieza cuando se dio de alta
  const cur   = new Date(now.getFullYear(), now.getMonth(), 1);
  const d     = new Date(start);
  let seq     = 1;
  while (d <= cur) {
    const y = d.getFullYear(), m = d.getMonth();
    const issueDate = lastDayOfMonth(y, m);
    const billed    = issueDate < now;
    const nn        = String(seq).padStart(3, "0");
    const mm        = String(m + 1).padStart(2, "0");
    invoices.push({
      number:    `FAC-${y}-${mm}-${String(orgIndex + 1).padStart(3, "0")}-${nn}`,
      period:    `${MONTH_NAMES_LONG[m]} ${y}`,
      issueDate,
      amount:    PLAN_PRICE[org.plan] ?? 0,
      status:    billed ? "paid" : "pending",
    });
    d.setMonth(d.getMonth() + 1);
    seq++;
  }
  return invoices;
}

// Cápsula de estado tintada cálida: punto + etiqueta en su color.
function StatusPill({ color, label }: { color: string; label: string }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: color + "1e", color,
      borderRadius: 999, padding: "3px 11px",
      fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {label}
    </span>
  );
}

// ── Invoice detail modal ──────────────────────────────────────────────────────

function OrgInvoiceModal({ org, orgIndex, onClose }: { org: Org; orgIndex: number; onClose: () => void }) {
  const now      = new Date();
  const invoices = buildInvoices(org, orgIndex, now);
  const meta     = PLAN_LABEL[org.plan] ?? { name: org.plan, level: "—", bg: "#6b728018", color: "#6b7280" };
  const paid     = invoices.filter(i => i.status === "paid");
  const totalPaid = paid.reduce((s, i) => s + i.amount, 0);
  const totalPending = invoices.filter(i => i.status === "pending").reduce((s, i) => s + i.amount, 0);

  const thI: React.CSSProperties = {
    padding: "0.6rem 1rem", fontSize: "0.65rem", fontWeight: 600,
    textTransform: "uppercase", letterSpacing: 0.9, color: "var(--clr-text4)",
    textAlign: "left", borderBottom: "1px solid var(--clr-border2)", whiteSpace: "nowrap",
    background: "var(--clr-modal-solid)",
  };
  const tdI: React.CSSProperties = {
    padding: "0.75rem 1rem", fontSize: "0.84rem", color: "var(--clr-text)",
    borderBottom: "1px solid var(--clr-border2)", verticalAlign: "middle",
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(11,18,21,0.4)",
        backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: "1.5rem",
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: "var(--clr-modal-solid)",
        border: "1px solid var(--clr-border)",
        borderRadius: 22, width: "100%", maxWidth: 720,
        boxShadow: "0 24px 80px -24px rgba(0,0,0,0.45)",
        display: "flex", flexDirection: "column", maxHeight: "88vh",
        overflow: "hidden",
        fontVariantNumeric: "tabular-nums",
      }}>

        {/* Header */}
        <div style={{ padding: "1.25rem 1.5rem", borderBottom: "1px solid var(--clr-border2)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700, letterSpacing: "-0.01em", color: "var(--clr-text)" }}>{org.name}</h2>
              <span style={{ background: meta.bg, color: meta.color, padding: "2px 10px", borderRadius: 20, fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase" }}>
                {meta.name}
              </span>
              <span style={{ fontSize: "0.75rem", color: "var(--clr-text3)" }}>{meta.level} · {formatEur(PLAN_PRICE[org.plan] ?? 0)}/mes</span>
            </div>
            <div style={{ display: "flex", gap: 20, marginTop: 8 }}>
              <div style={{ fontSize: "0.78rem", color: "var(--clr-text3)" }}>
                <span style={{ fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)", fontSize: "0.95rem" }}>{formatEur(totalPaid)}</span>
                {" "}cobrado total
              </div>
              <div style={{ fontSize: "0.78rem", color: "var(--clr-text3)" }}>
                {paid.length} facturas emitidas
              </div>
              <div style={{ fontSize: "0.78rem", color: "var(--clr-text3)" }}>
                {totalPending > 0 && (
                  <><span style={{ fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)", fontSize: "0.95rem" }}>{formatEur(PLAN_PRICE[org.plan] ?? 0)}</span>{" "}pendiente</>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            style={{
              width: 30, height: 30, borderRadius: 15,
              background: "var(--clr-modal-solid2)", border: "1px solid var(--clr-border2)",
              cursor: "pointer", color: "var(--clr-text3)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "0.8rem", lineHeight: 1, flexShrink: 0,
            }}
          >✕</button>
        </div>

        {/* Table */}
        <div style={{ overflowY: "auto", flex: 1 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
              <tr>
                <th style={thI}>Nº Factura</th>
                <th style={thI}>Periodo</th>
                <th style={thI}>Fecha emisión</th>
                <th style={{ ...thI, textAlign: "right" }}>Importe</th>
                <th style={{ ...thI, textAlign: "center" }}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv, idx) => (
                <tr
                  key={inv.number}
                  style={{ transition: "background 0.1s" }}
                  onMouseEnter={e => (e.currentTarget.style.background = "var(--clr-hover)")}
                  onMouseLeave={e => (e.currentTarget.style.background = "")}
                >
                  <td style={{ ...tdI, borderBottom: idx === invoices.length - 1 ? "none" : undefined, fontSize: "0.78rem", color: "var(--clr-text3)" }}>
                    {inv.number}
                  </td>
                  <td style={{ ...tdI, borderBottom: idx === invoices.length - 1 ? "none" : undefined, fontWeight: 600, color: "var(--clr-text)" }}>
                    {inv.period}
                  </td>
                  <td style={{ ...tdI, borderBottom: idx === invoices.length - 1 ? "none" : undefined, color: "var(--clr-text3)", fontSize: "0.82rem" }}>
                    {formatDate(inv.issueDate)}
                    {inv.status === "pending" && <span style={{ fontSize: "0.72rem", color: "var(--clr-text4)" }}> (pendiente)</span>}
                  </td>
                  <td style={{ ...tdI, borderBottom: idx === invoices.length - 1 ? "none" : undefined, textAlign: "right", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)" }}>
                    {formatEur(inv.amount)}
                  </td>
                  <td style={{ ...tdI, borderBottom: idx === invoices.length - 1 ? "none" : undefined, textAlign: "center" }}>
                    <StatusPill color={inv.status === "paid" ? "#16a34a" : "#d97706"} label={inv.status === "paid" ? "Cobrada" : "Pendiente"} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer totals */}
        <div style={{
          borderTop: "1px solid var(--clr-border2)", padding: "1rem 1.5rem",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex" }}>
            {[
              { label: "Total cobrado",   value: totalPaid,                                       show: true },
              { label: "Pendiente",       value: totalPending,                                    show: totalPending > 0 },
              { label: "Total acumulado", value: invoices.reduce((s, i) => s + i.amount, 0),      show: true },
            ].filter(x => x.show).map(({ label, value }, i) => (
              <div key={label} style={{ paddingLeft: i > 0 ? 24 : 0, marginLeft: i > 0 ? 24 : 0, borderLeft: i > 0 ? "1px solid var(--clr-border2)" : "none" }}>
                <div style={{ fontSize: "0.62rem", color: "var(--clr-text4)", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: "1.15rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)" }}>{formatEur(value)}</div>
              </div>
            ))}
          </div>
          <button
            onClick={onClose}
            style={{
              padding: "0.5rem 1.3rem", border: "1px solid var(--clr-border2)", borderRadius: 999,
              background: "var(--clr-modal-solid2)", color: "var(--clr-text2)",
              cursor: "pointer", fontSize: "0.8rem", fontWeight: 600,
            }}
          >
            Cerrar
          </button>
        </div>

      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

// Orden de apilado (base → cima): Core abajo, Ultra arriba.
const PLAN_STACK_ORDER = ["core", "pro", "ultra"];

function RevenueChart({ months }: { months: MonthData[] }) {
  if (months.length === 0) return null;
  const maxRev  = Math.max(...months.map(m => m.revenue), 1);
  const tickStep = maxRev <= 2000 ? 500 : maxRev <= 5000 ? 1000 : 2000;
  const topTick  = Math.ceil(maxRev / tickStep) * tickStep;
  const ticks    = Array.from({ length: topTick / tickStep + 1 }, (_, i) => i * tickStep);

  return (
    <div>
      {/* Área de trazado: gridlines y barras comparten la MISMA referencia (0€ = bottom) */}
      <div style={{ position: "relative", height: 200 }}>
        {ticks.map(tick => (
          <div key={tick} style={{ position: "absolute", bottom: `${(tick / topTick) * 100}%`, left: 0, right: 0, display: "flex", alignItems: "center", gap: 8, transform: "translateY(50%)", pointerEvents: "none" }}>
            <span style={{ fontSize: "0.68rem", color: "var(--clr-text4)", whiteSpace: "nowrap", minWidth: 46, textAlign: "right" }}>{formatEurShort(tick)}</span>
            <div style={{ flex: 1, height: 1, background: "var(--clr-border2)", opacity: tick === 0 ? 1 : 0.6 }} />
          </div>
        ))}
        <div style={{ position: "absolute", top: 0, right: 0, bottom: 0, left: 62, display: "flex", gap: 10 }}>
          {months.map(m => {
            const pct = topTick > 0 ? Math.max((m.revenue / topTick) * 100, 2) : 2;
            return (
              <div key={`${m.year}-${m.month}`} style={{ flex: 1, position: "relative" }}>
                {/* Cifra del mes, justo encima de la barra */}
                <div style={{ position: "absolute", bottom: `calc(${pct}% + 5px)`, left: 0, right: 0, textAlign: "center", fontSize: "0.7rem", fontWeight: 600, color: m.billed ? "var(--clr-text2)" : "var(--clr-text4)" }}>
                  {m.revenue > 0 ? formatEurShort(m.revenue) : "—"}
                </div>
                {m.billed ? (
                  /* Barra apilada por plan (Core abajo → Ultra arriba) */
                  <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: `${pct}%`, display: "flex", flexDirection: "column", borderRadius: "6px 6px 0 0", overflow: "hidden" }}>
                    {PLAN_STACK_ORDER.slice().reverse().map(plan => {
                      const amt = m.byPlan[plan] ?? 0;
                      if (amt <= 0 || m.revenue <= 0) return null;
                      return <div key={plan} style={{ height: `${(amt / m.revenue) * 100}%`, background: PLAN_LABEL[plan].color }} />;
                    })}
                  </div>
                ) : (
                  <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: `${pct}%`, borderRadius: "6px 6px 0 0", background: "var(--clr-surface2)", border: "1px dashed var(--clr-border2)", borderBottom: "none", boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: "0.6rem", color: "var(--clr-text4)", fontWeight: 600, letterSpacing: 0.5 }}>PDTE</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {/* Eje X: etiquetas de mes, fuera del área de trazado */}
      <div style={{ marginLeft: 62, display: "flex", gap: 10, marginTop: 6 }}>
        {months.map(m => (
          <div key={`${m.year}-${m.month}`} style={{ flex: 1, textAlign: "center", fontSize: "0.72rem", color: "var(--clr-text3)", fontWeight: m.billed ? 400 : 600 }}>{m.shortLabel}</div>
        ))}
      </div>
      {/* Leyenda por plan */}
      <div style={{ marginLeft: 62, display: "flex", gap: 16, marginTop: 14, flexWrap: "wrap" }}>
        {PLAN_STACK_ORDER.map(plan => (
          <div key={plan} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: PLAN_LABEL[plan].color }} />
            <span style={{ fontSize: "0.72rem", color: "var(--clr-text4)" }}>{PLAN_LABEL[plan].name}</span>
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 12, height: 12, borderRadius: 3, border: "1px dashed var(--clr-border2)", background: "var(--clr-surface2)", boxSizing: "border-box" }} />
          <span style={{ fontSize: "0.72rem", color: "var(--clr-text4)" }}>Pendiente</span>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SuperAdminFacturacionPage() {
  const [orgs, setOrgs]           = useState<Org[]>([]);
  const [loading, setLoading]     = useState(true);
  const [selectedOrg, setSelected] = useState<{ org: Org; idx: number } | null>(null);

  const now           = new Date();
  const months        = buildMonths(orgs, now);
  const billedMonths  = months.filter(m => m.billed);
  const currentMonth  = months.find(m => !m.billed);
  const nextDate      = lastDayOfMonth(now.getFullYear(), now.getMonth());

  const mrr     = orgs.filter(o => o.is_active).reduce((s, o) => s + (PLAN_PRICE[o.plan] ?? 0), 0);
  const arr     = mrr * 12;
  const total   = billedMonths.reduce((s, m) => s + m.revenue, 0);
  const pending = currentMonth?.revenue ?? mrr;

  useEffect(() => {
    api.get<Org[]>("/organizations")
      .then(data => { setOrgs(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const card: React.CSSProperties = {
    background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18,
    backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
    boxShadow: "var(--clr-shadow-sm)",
  };

  const thStyle: React.CSSProperties = {
    padding: "0.6rem 1rem", fontSize: "0.65rem", fontWeight: 600,
    textTransform: "uppercase", letterSpacing: 0.9, color: "var(--clr-text4)",
    textAlign: "left", borderBottom: "1px solid var(--clr-border2)", whiteSpace: "nowrap",
  };
  const tdStyle: React.CSSProperties = {
    padding: "0.85rem 1rem", fontSize: "0.84rem", color: "var(--clr-text)",
    borderBottom: "1px solid var(--clr-border2)", verticalAlign: "middle",
  };

  if (loading) {
    return <div style={{ padding: "2rem", textAlign: "center", color: "var(--clr-text4)" }}>Cargando datos de facturación…</div>;
  }

  return (
    <div style={{ padding: "1.75rem 2rem", maxWidth: 1100, margin: "0 auto", fontVariantNumeric: "tabular-nums" }}>

      {/* Invoice detail modal */}
      {selectedOrg && (
        <OrgInvoiceModal
          org={selectedOrg.org}
          orgIndex={selectedOrg.idx}
          onClose={() => setSelected(null)}
        />
      )}

      {/* Header */}
      <div style={{ marginBottom: "1.75rem" }}>
        <h1 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)" }}>Facturación</h1>
        <p style={{ margin: "0.35rem 0 0", fontSize: "0.82rem", color: "var(--clr-text3)" }}>
          Ingresos recurrentes · Sin IVA · Cobro a final de mes
        </p>
      </div>

      {/* KPIs: un panel de vidrio con franja de datos (números en tinta) */}
      <div style={{
        ...card, padding: "1.1rem 1.4rem", marginBottom: "1.5rem",
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
      }}>
        {[
          { label: "MRR actual",          value: formatEur(mrr),     sub: `${orgs.filter(o => o.is_active).length} clientes activos` },
          { label: "ARR proyectado",      value: formatEur(arr),     sub: "A planes actuales" },
          { label: "Total cobrado 2026",  value: formatEur(total),   sub: `${billedMonths.length} ${billedMonths.length === 1 ? "mes facturado" : "meses facturados"}` },
          { label: "Próxima facturación", value: formatEur(pending), sub: nextDate.toLocaleDateString("es-ES", { day: "numeric", month: "long" }) },
        ].map(({ label, value, sub }, i) => (
          <div key={label} style={{
            paddingLeft: i > 0 ? 18 : 0,
            borderLeft: i > 0 ? "1px solid var(--clr-border2)" : "none",
          }}>
            <div style={{ fontSize: "1.55rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)", lineHeight: 1.05 }}>
              {value}
            </div>
            <div style={{ fontSize: "0.64rem", color: "var(--clr-text4)", marginTop: 4, textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</div>
            {sub && <div style={{ fontSize: "0.7rem", color: "var(--clr-text4)", marginTop: 3 }}>{sub}</div>}
          </div>
        ))}
      </div>

      {/* Chart */}
      <div style={{ ...card, padding: "1.5rem", marginBottom: "1.5rem" }}>
        <div style={{ marginBottom: "1.25rem", display: "flex", alignItems: "baseline", gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, letterSpacing: "-0.01em", color: "var(--clr-text)" }}>Evolución de ingresos</h2>
          <span style={{ fontSize: "0.78rem", color: "var(--clr-text4)" }}>
            Enero 2026 → {MONTH_NAMES_LONG[now.getMonth()]} {now.getFullYear()}
          </span>
        </div>
        <RevenueChart months={months} />
      </div>

      {/* Plan breakdown */}
      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>
        {Object.entries(PLAN_LABEL).map(([plan, meta]) => {
          const count = orgs.filter(o => o.plan === plan && o.is_active).length;
          return (
            <div key={plan} style={{ ...card, padding: "1rem 1.25rem", flex: 1, minWidth: 160 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  <span style={{ background: meta.bg, color: meta.color, padding: "2px 10px", borderRadius: 20, fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase" }}>{meta.name}</span>
                  <span style={{ fontSize: "0.72rem", color: "var(--clr-text4)" }}>{meta.level}</span>
                </span>
                <span style={{ fontSize: "0.72rem", fontWeight: 500, color: "var(--clr-text3)" }}>{count} {count === 1 ? "org" : "orgs"}</span>
              </div>
              <div style={{ fontSize: "1.3rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)" }}>
                {formatEur(PLAN_PRICE[plan])}<span style={{ fontSize: "0.75rem", fontWeight: 400, color: "var(--clr-text4)" }}>/mes</span>
              </div>
              <div style={{ fontSize: "0.75rem", color: "var(--clr-text4)", marginTop: 4 }}>
                {formatEur(PLAN_PRICE[plan] * count)}/mes · {formatEur(PLAN_PRICE[plan] * count * billedMonths.length)} cobrado
              </div>
            </div>
          );
        })}
      </div>

      {/* Org table */}
      <div style={{ ...card, overflow: "hidden" }}>
        <div style={{ padding: "1.25rem 1.5rem 0.75rem", borderBottom: "1px solid var(--clr-border2)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, letterSpacing: "-0.01em", color: "var(--clr-text)" }}>Detalle por organización</h2>
            <p style={{ margin: "4px 0 0", fontSize: "0.76rem", color: "var(--clr-text4)" }}>Haz clic en una fila para ver el historial de facturas</p>
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Organización</th>
                <th style={thStyle}>Plan</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Tarifa/mes</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Meses facturados</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Total cobrado</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Pendiente mes actual</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Estado</th>
                <th style={{ ...thStyle, textAlign: "center", width: 48 }}></th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((org, i) => {
                const meta      = PLAN_LABEL[org.plan] ?? { name: org.plan, level: "—", bg: "#6b728018", color: "#6b7280" };
                const price     = PLAN_PRICE[org.plan] ?? 0;
                const orgInvs   = buildInvoices(org, i, now);
                const orgPaid   = orgInvs.filter(inv => inv.status === "paid");
                const orgTotal  = orgPaid.reduce((s, inv) => s + inv.amount, 0);
                const isLast    = i === orgs.length - 1;

                return (
                  <tr
                    key={org.id}
                    onClick={() => setSelected({ org, idx: i })}
                    style={{ cursor: "pointer", transition: "background 0.12s" }}
                    onMouseEnter={e => (e.currentTarget.style.background = "var(--clr-hover)")}
                    onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                  >
                    <td style={{ ...tdStyle, borderBottom: isLast ? "none" : undefined, fontWeight: 600 }}>{org.name}</td>
                    <td style={{ ...tdStyle, borderBottom: isLast ? "none" : undefined }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ background: meta.bg, color: meta.color, padding: "2px 8px", borderRadius: 20, fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase" }}>{meta.name}</span>
                        <span style={{ fontSize: "0.75rem", color: "var(--clr-text4)" }}>{meta.level}</span>
                      </div>
                    </td>
                    <td style={{ ...tdStyle, borderBottom: isLast ? "none" : undefined, textAlign: "right" }}>{formatEur(price)}</td>
                    <td style={{ ...tdStyle, borderBottom: isLast ? "none" : undefined, textAlign: "right", color: "var(--clr-text3)" }}>{orgPaid.length}</td>
                    <td style={{ ...tdStyle, borderBottom: isLast ? "none" : undefined, textAlign: "right", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)" }}>{formatEur(orgTotal)}</td>
                    <td style={{ ...tdStyle, borderBottom: isLast ? "none" : undefined, textAlign: "right", color: "var(--clr-text2)" }}>
                      {org.is_active ? formatEur(price) : "—"}
                    </td>
                    <td style={{ ...tdStyle, borderBottom: isLast ? "none" : undefined, textAlign: "center" }}>
                      <StatusPill color={org.is_active ? "#16a34a" : "#64748b"} label={org.is_active ? "Activo" : "Inactivo"} />
                    </td>
                    <td style={{ ...tdStyle, borderBottom: isLast ? "none" : undefined, textAlign: "center" }}>
                      <span style={{ color: "var(--clr-text4)", fontSize: "0.8rem", opacity: 0.7 }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 18l6-6-6-6"/>
                        </svg>
                      </span>
                    </td>
                  </tr>
                );
              })}

              {/* Totals row */}
              <tr style={{ borderTop: "1px solid var(--clr-border2)" }}>
                <td colSpan={2} style={{ ...tdStyle, borderBottom: "none", fontWeight: 600, fontSize: "0.7rem", color: "var(--clr-text4)", textTransform: "uppercase", letterSpacing: 0.8 }}>Total</td>
                <td style={{ ...tdStyle, borderBottom: "none", textAlign: "right", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)" }}>{formatEur(mrr)}</td>
                <td style={{ ...tdStyle, borderBottom: "none", textAlign: "right", color: "var(--clr-text3)" }}>—</td>
                <td style={{ ...tdStyle, borderBottom: "none", textAlign: "right", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)" }}>{formatEur(total)}</td>
                <td style={{ ...tdStyle, borderBottom: "none", textAlign: "right", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)" }}>{formatEur(pending)}</td>
                <td colSpan={2} style={{ ...tdStyle, borderBottom: "none" }} />
              </tr>
            </tbody>
          </table>
        </div>

        <div style={{ padding: "0.75rem 1.5rem", borderTop: "1px solid var(--clr-border2)", display: "flex", alignItems: "center", gap: 6 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ color: "var(--clr-text4)", flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>
          </svg>
          <span style={{ fontSize: "0.72rem", color: "var(--clr-text4)" }}>
            Precios sin IVA · Inicio de servicio: enero 2026 · Cobro a final de cada mes
          </span>
        </div>
      </div>

    </div>
  );
}
